import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * A surface on the workspace (DESIGN.md §6): elevation is a stroke, not a blur.
 * `raised` is the sunken ground the order line and the tender panel sit on.
 * The Ledger Ink `--lift` shadow is retired; the only shadow left in the system
 * belongs to the overlay.
 *
 * Anything else a caller passes reaches the element. It used to be dropped, which
 * cost more than a test hook: six panels across the app pass an `aria-label` —
 * "Refund computation", "Shift summary", "Denomination counter" — and a screen
 * reader was told none of them. Found when a `data-testid` on a panel was not in
 * the DOM; the accessibility labels had been silently missing all along.
 */
export function Panel({
  children,
  className,
  raised = false,
  as: Tag = "div",
  ...rest
}: Readonly<
  React.HTMLAttributes<HTMLElement> & {
    children: React.ReactNode;
    className?: string;
    raised?: boolean;
    as?: "div" | "section" | "aside";
  }
>) {
  return (
    <Tag
      {...rest}
      className={cn(
        "rounded-tile border border-stroke-weak",
        raised ? "bg-bg-raised" : "bg-bg-base",
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * The tracked eyebrow above a section (DESIGN.md §5): 14 px is the floor on a
 * till, so this is the one place capitals are allowed below 16.
 */
export function SectionLabel({
  children,
  className,
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <div className={cn("font-sans text-sm font-semibold uppercase tracking-[0.08em] text-text-weak", className)}>
      {children}
    </div>
  );
}

/** The one screen title (DESIGN.md §5): Bricolage 24, one per screen. */
export function ScreenTitle({
  children,
  className,
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <h1 className={cn("font-display text-2xl font-bold tracking-[-0.02em] text-text-strong", className)}>
      {children}
    </h1>
  );
}
